/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  CSSProperties,
  MouseEvent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef
} from 'react';
import {
  ListItem,
  makeStyles,
  withStyles
} from '@material-ui/core';
import classnames from 'classnames';
import Device from '@ion/core/src/services/Device';
import {GlobalSearchResult} from '@ion/core/src/services/GlobalSearch';
import {Crumb, CVSAssetType, Devices, HVSRecord} from '@ion/core/src/types/entities';
import {AssetSummaryCard} from '@ion/dls/src/components/assetcard/current/AssetSummaryCard';
import {dlsAlt, dlsColor} from '@ion/dls/src/theme/theme';
import {isCurrentTheme} from '@ion/dls/src/theme/useThemeContext';
import {AssetLabelTypes, SideSheetView} from '@ion/dls/src/types/types';
import {getAssetSummaryCardProps} from './getAssetSummaryCardProps';
import {LegacyAssetCardContent} from './LegacyAssetCardContent';

export type AssetCardProps = {
  asset: Device;
  handleListItemClick?: (asset: object) => void;
  sideSheetView?: SideSheetView;
  onHover?: (asset: CVSAssetType) => void;
  onMouseLeave?: () => void;
  hoveredAsset?: {
    asset: CVSAssetType,
    source: string;
  };
  selectedAsset?: Device | HVSRecord;
  isMapSelection?: boolean;
  detailOnClick?: (e: MouseEvent<HTMLSpanElement>, asset: Devices) => void;
  assetDetailView?: boolean;
  toolDetailView?: boolean;
  renderList?: (props: any) => ReactElement;
  toolConnected?: boolean;
  labels?: AssetLabelTypes;
  onContainsClick?: (asset: CVSAssetType) => void;
  toolPanelOpen?: {
    assetId?: string | null;
    open?: boolean;
  }[];
  panelLoading?: number | null;
  crumbs?: Crumb[];
  setCrumbs?: Function;
  handleCrumbClick?: Function;
  ContainedComponent?: ReactElement;
  shouldScroll?: boolean;
  setShouldScroll?: Function;
  searchSelected?: GlobalSearchResult;
  backgroundColor?: string;
  isSideSheetOpen?: boolean;
  onDeviceEdit?: Function;
  assignedAssetsPanelsOpen?: string;
  onSelectAssociatedAsset?: Function;
  onToggleAssignedAssetsPanel?: Function;
  // changing it re-runs the scroll-into-view check (unset: only a new
  // selection does) ...
  scrollCheckKey?: number;
}

// a caller's background reaches the card through this custom property, so
// the card's own rules (hover included) still apply on top of it ...
const BACKGROUND_PROPERTY = '--asset-card-background';

const useStyles = makeStyles(() => {
  const borderStyle = `1px solid ${dlsColor('grey5')}`;
  return {
    // static rules only - prop-driven values made JSS add a dynamic rule
    // per mounted card; the detail view variant and the caller's background
    // are applied with a class and a custom property instead ...
    root : {
      width           : '100%',
      backgroundColor : `var(${BACKGROUND_PROPERTY}, ${dlsColor('white')})`,
      borderTop       : borderStyle,
      borderBottom    : borderStyle,
      justifyContent  : 'space-between',
      cursor          : 'pointer',
      '&:hover'       : {
        backgroundColor : dlsColor('paleBlue')
      },
      '&.MuiList-padding' : {
        paddingBottom : '0px'
      }
    },
    rootDetailView : {
      borderTop : 'none',
      '&:hover' : {
        backgroundColor : dlsColor('white')
      }
    },
    isHovered : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')} !important`,
      '& p'           : {
        color : dlsColor('grey1')
      }
    },
    // per the Asset Summary Card's "Hover & Selected States" spec - current
    // theme only, distinct from the legacy `isHovered`/`&$selected` treatment
    // above (which uses the same color for both states) ...
    currentThemeHover : {
      '&:hover' : {
        backgroundColor : `${dlsColor('grey8')} !important`
      }
    },
    currentThemeSelected : {
      backgroundColor : `${dlsColor('paleBlue')} !important`
    },
    cursor : {
      cursor : 'pointer'
    },
    // AssetSummaryCard owns its own padding (16px 24px, matching its Figma
    // design exactly) - this suppresses StyledListItem's legacy 12px 15px so
    // the two don't stack into more padding than either design intends ...
    noItemPadding : {
      padding : '0 !important'
    },
    itemPointerEvents : {
      pointerEvents : 'auto'
    },
    container : {
      width  : '100%',
      height : '100%'
    }
  };
});

const StyledListItem = withStyles(theme => ({
  root : {
    padding        : '12px 15px',
    justifyContent : 'space-between',
    '&$selected'   : {
      backgroundColor : `${dlsAlt('components', 'icon', 'selected')}`
    }
  },
  selected : {}
}))(ListItem);

/**
 *  Defines the AssetCard component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 * import React from 'react';
 * import AssetCard from '@ion/dls/src/components/cards/AssetCard';
 *
 * <AssetCard asset={data} />
 *
 * ```
 */
const AssetCard = (props: AssetCardProps) => {
  const {
    handleListItemClick,
    asset: currentAsset,
    sideSheetView,
    onHover,
    onMouseLeave,
    selectedAsset,
    isMapSelection,
    detailOnClick,
    assetDetailView,
    toolDetailView,
    renderList,
    toolConnected = true,
    shouldScroll,
    setShouldScroll,
    searchSelected,
    backgroundColor,
    isSideSheetOpen,
    onDeviceEdit,
    scrollCheckKey
  } = props;
  const classes = useStyles();
  const style   = useMemo(() => (
    backgroundColor ? {[BACKGROUND_PROPERTY]: backgroundColor} as CSSProperties : undefined
  ), [backgroundColor]);
  const cardRef = useRef(null);
  const asset   = currentAsset instanceof Device ? currentAsset : new Device(currentAsset);
  const {
    assetId,
    name: toolName
  } = asset?.getDevice();

  useEffect(() => {
    if (selectedAsset) {
      const {assetId: selectedAssetId} = selectedAsset instanceof Device ? selectedAsset.getDevice() : selectedAsset;
      const isSelectedAsset = assetId === selectedAssetId;
      const isSearchedAsset = assetId === searchSelected?.item?.assetId;
      const isMapScroll     = isMapSelection && shouldScroll;
      const notDetailsOrMap = !assetDetailView && !isMapSelection;
      if ((isSelectedAsset && isMapScroll) || (isSearchedAsset && notDetailsOrMap && shouldScroll)) {
        cardRef?.current?.scrollIntoView();
        setTimeout(function() {
          cardRef?.current?.scrollIntoView();
          isSideSheetOpen && setShouldScroll(false);
        }, 400);
      }
    }
  }, [selectedAsset, scrollCheckKey]);

  const handleItemClick = useCallback(function(event) {
    event.stopPropagation();
    handleListItemClick?.(asset);
  }, [asset, handleListItemClick]);

  const onEnter = onHover && function() {
    asset.isCVSAsset() && onHover(asset.getDevice());
  };

  const getSelectedAsset = useCallback((asset: Device | HVSRecord) => {
    if (asset instanceof Device) {
      return asset?.getDevice();
    }
    return asset;
  }, []);

  const isDeviceOrAssetSelected = useMemo(() => (
    asset?.isScDevice()
      ? asset?.getItemValue({
        deviceAttribute : undefined,
        cvsAttribute    : 'deviceObjectId'
      }) === getSelectedAsset(selectedAsset)?.deviceObjectId
      : getSelectedAsset(selectedAsset)?.assetId === assetId
  ), [asset, getSelectedAsset, selectedAsset, assetId]);

  const isMapHovered = useMemo(() => (
    isDeviceOrAssetSelected && (!assetDetailView && !toolDetailView)
  ), [isDeviceOrAssetSelected, assetDetailView, toolDetailView]);

  // only computed for the current theme - the legacy markup below is left
  // entirely as-is otherwise. Tool assets have no vehicle-specific health/
  // alert data, so getHealthIndicatorValues/getAlertIndicatorValues simply
  // come back empty for them and AssetSummaryCard's Diagnostics section
  // self-omits, same as any other asset with nothing to show there ...
  // a tool's card keeps the card content that shows its tag details - the
  // Asset Summary Card reads an asset's own fields (status time, location,
  // driver), which a tool doesn't have, and left its detail view empty ...
  const isToolCard       = !!toolDetailView || 'toolPanelLabels' in props;
  const summaryCardProps = useMemo(() => (
    isCurrentTheme() && !isToolCard ? getAssetSummaryCardProps(asset, {
      onAssetNameClick : function(event?: MouseEvent<HTMLButtonElement>) {
        event?.stopPropagation();
        if (!assetDetailView && detailOnClick) {
          detailOnClick(event as any, asset);
        }
      }
    }) : null
  ), [asset, assetDetailView, detailOnClick, isToolCard]);

  return (
    <div className={
      classnames(
        classes.root,
        assetDetailView && classes.rootDetailView,
        summaryCardProps && classes.currentThemeHover,
        isMapHovered && (summaryCardProps ? classes.currentThemeSelected : classes.isHovered),
        classes.itemPointerEvents
      )
    } style={style} onMouseEnter={onEnter} onMouseLeave={onMouseLeave} ref={cardRef}>
      <StyledListItem disableRipple
        className={classnames(classes.cursor, classes.itemPointerEvents)}
        classes={summaryCardProps ? {root: classes.noItemPadding} : undefined}
        button
        onClick={handleItemClick}>
        <div className={classes.container}>
          {summaryCardProps ? (
            // Assigned Assets/Tags are intentionally not shown on the new
            // Asset Summary Card - ION-7392 never scoped a section for them ...
            <AssetSummaryCard {...summaryCardProps} />
          ) : (
            <LegacyAssetCardContent asset={asset}
              sideSheetView={sideSheetView}
              assetDetailView={assetDetailView}
              toolConnected={toolConnected}
              detailOnClick={detailOnClick}
              onDeviceEdit={onDeviceEdit}
              renderList={renderList}
              cardProps={props} />
          )}
        </div>
      </StyledListItem>
    </div>
  );
};

export default AssetCard;
